import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useProperty, useTimeline } from '../hooks/useData';
import { propertyAPI, timelineAPI } from '../services/api';
import { GradePill, Chip, Spinner, EmptyState, Panel, fmtDate, fmtCost, TYPE_META } from '../components/common/UIComponents';
import PropertyCalculatorTab from '../components/property/PropertyCalculatorTab';
import { PendingDeletionBanner, PropertyDangerMenu } from '../components/property/PropertyLifecycle';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// ── Upload constraints (must match backend config MAX_FILE_SIZE_MB = 5) ──────
const MAX_UPLOAD_BYTES = 5 * 1024 * 1024; // 5 MB
const MAX_UPLOAD_LABEL = '5 MB';

// accept strings for <input type="file"> — restricts the OS file picker
const ACCEPT_IMAGES = 'image/jpeg,image/png,.jpg,.jpeg,.png';
const ACCEPT_DOCS = [
  'application/pdf,.pdf',
  'application/msword,.doc',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document,.docx',
  'application/vnd.ms-excel,.xls',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,.xlsx',
  'application/vnd.ms-powerpoint,.ppt',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation,.pptx',
].join(',');
const ACCEPT_ALL = `${ACCEPT_IMAGES},${ACCEPT_DOCS}`;

function validateUploadFile(file) {
  if (file.size > MAX_UPLOAD_BYTES) {
    return `"${file.name}" exceeds the ${MAX_UPLOAD_LABEL} limit (${(file.size / 1_048_576).toFixed(1)} MB)`;
  }
  return null;
}

function fmtAddress(p) {
  if (p.country === 'AU') return `${p.city} ${p.state} ${p.zip_code}`;
  return `${p.city}, ${p.state} ${p.zip_code}`;
}

const OCCUPANCY_STATUS_OPTS = [
  { value: '', label: 'Not set' },
  { value: 'owner_occupied', label: 'Owner Occupied' },
  { value: 'rented', label: 'Rented' },
  { value: 'vacant', label: 'Vacant' },
  { value: 'for_lease', label: 'For Lease' },
  { value: 'for_sale', label: 'For Sale' },
];

const OCCUPANCY_CFG = {
  owner_occupied: { label: 'Owner Occupied', bg: 'rgba(31,58,46,0.1)', color: 'var(--forest)' },
  rented:         { label: 'Rented',         bg: 'rgba(37,99,235,0.1)', color: '#1d4ed8' },
  vacant:         { label: 'Vacant',         bg: 'rgba(220,38,38,0.1)', color: '#dc2626' },
  for_lease:      { label: 'For Lease',      bg: 'rgba(234,179,8,0.1)', color: '#b45309' },
  for_sale:       { label: 'For Sale',       bg: 'rgba(147,51,234,0.1)', color: '#7c3aed' },
};

function OccupancyBadge({ status }) {
  const cfg = OCCUPANCY_CFG[status];
  if (!cfg) return null;
  return (
    <span style={{
      fontSize: 11, padding: '3px 10px', borderRadius: 12, fontWeight: 600,
      background: cfg.bg, color: cfg.color,
      border: `1px solid ${cfg.color}33`,
    }}>{cfg.label}</span>
  );
}

const FEATURES = [
  'Air Conditioning', 'Central Heating', 'Pool', 'Spa / Hot Tub',
  'Patio / Deck', 'Garage', 'Fireplace', 'Solar Panels',
  'Security System', 'Garden', 'Balcony', 'Storage / Shed',
  'Gym', 'Home Theatre', 'Smart Home', 'EV Charging',
];

const PRO_ROLES = [
  'Real Estate Agent', 'Property Manager', 'Accountant / Tax Agent',
  'Solicitor / Conveyancer', 'Mortgage Broker', 'Insurance Agent',
  'Building Inspector', 'Strata Manager', 'Financial Advisor', 'Other',
];

const FIN_CATEGORIES = [
  'Mortgage / Loan', 'Insurance', 'Council Rates', 'Strata / Body Corp',
  'Maintenance', 'Renovation', 'Landscaping', 'Cleaning', 'Property Management',
  'Rental Income', 'Other Income', 'Other Expense',
];

const DOC_TYPES = [
  { value: 'other', label: 'Other' },
  { value: 'contract', label: 'Contract / Agreement' },
  { value: 'title', label: 'Title Deed' },
  { value: 'insurance', label: 'Insurance Policy' },
  { value: 'inspection', label: 'Inspection Report' },
  { value: 'plans', label: 'Building Plans' },
  { value: 'invoice', label: 'Invoice / Receipt' },
  { value: 'warranty', label: 'Warranty' },
  { value: 'permit', label: 'Permit / Certificate' },
  { value: 'tax', label: 'Tax Document' },
];

const PROP_TYPES = [
  { value: 'single_family', label: 'Single Family' },
  { value: 'condo', label: 'Condo / Apartment' },
  { value: 'townhouse', label: 'Townhouse' },
  { value: 'multi_family', label: 'Multi-Family' },
  { value: 'commercial', label: 'Commercial' },
  { value: 'land', label: 'Land' },
  { value: 'other', label: 'Other' },
];

const AU_STATES = ['NSW', 'VIC', 'QLD', 'WA', 'SA', 'TAS', 'NT', 'ACT'];

const TRADES = [
  '', 'Electrician', 'Plumber', 'HVAC', 'Roofer', 'Painter', 'Carpenter',
  'General Contractor', 'Landscaper', 'Mason', 'Tile & Flooring',
  'Windows & Doors', 'Pest Control', 'Appliance Repair', 'Cleaning Service', 'Inspector', 'Other',
];

const fieldStyle = {
  height: 38, padding: '0 10px',
  border: '1px solid var(--hairline)', borderRadius: 4,
  background: '#fff', fontSize: 13, outline: 'none', width: '100%', boxSizing: 'border-box',
};

const sectionLabel = {
  fontSize: 10, letterSpacing: '0.16em', textTransform: 'uppercase',
  color: 'var(--ink-4)', fontWeight: 600, marginBottom: 10,
  paddingBottom: 6, borderBottom: '1px solid var(--hairline)',
};

// ── Tab bar ─────────────────────────────────────────────────────────────────
function TabBar({ active, onChange, tabs }) {
  return (
    <div className="tab-bar" style={{ display: 'flex', gap: 0, borderBottom: '1px solid var(--hairline)', marginBottom: 24 }}>
      {tabs.map(({ key, label }) => (
        <button
          key={key}
          onClick={() => onChange(key)}
          style={{
            padding: '10px 20px', fontSize: 13, fontWeight: active === key ? 700 : 400,
            background: 'none', border: 'none', cursor: 'pointer',
            color: active === key ? 'var(--forest)' : 'var(--ink-4)',
            borderBottom: active === key ? '2px solid var(--forest)' : '2px solid transparent',
            marginBottom: -1, whiteSpace: 'nowrap',
          }}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function PropertyDetailPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { property, loading: propLoading, error: propError, refetch: refetchProp } = useProperty(id);
  const initialTab = new URLSearchParams(location.search).get('tab') || 'overview';
  const [tab, setTab] = useState(initialTab);
  const [loaded, setLoaded] = useState({});

  // Tab data
  const [images, setImages] = useState([]);
  const [proTags, setProTags] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [financials, setFinancials] = useState([]);
  const [accessGrants, setAccessGrants] = useState([]);
  const [utilities, setUtilities] = useState([]);

  // Modals
  const [editModal, setEditModal] = useState(false);
  const [addTagModal, setAddTagModal] = useState(false);
  const [addFinModal, setAddFinModal] = useState(false);
  const [editFinEntry, setEditFinEntry] = useState(null);
  const [showAddEntry, setShowAddEntry] = useState(false);
  const [editEntry, setEditEntry] = useState(null);

  const isOwner = user?.id === property?.owner_id;
  const [myAccess, setMyAccess] = useState(null);
  useEffect(() => {
    if (id) {
      propertyAPI.getMyAccess(id).then(({ data }) => setMyAccess(data)).catch(() => {});
    }
  }, [id]);
  const canWrite = isOwner || myAccess?.access_level === 'editor' || myAccess?.access_level === 'admin';
  const canViewFinancials = isOwner || myAccess?.can_view_financials;

  // Timeline state
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const { entries, total, loading: entriesLoading, refetch: refetchTimeline } = useTimeline(id, {
    entry_type: filter !== 'all' ? filter : undefined,
    search: search || undefined,
  });

  // Load images immediately for the header thumbnail strip
  useEffect(() => {
    if (id) {
      propertyAPI.getImages(id).then(({ data }) => {
        setImages(data);
        setLoaded((prev) => ({ ...prev, photos: true }));
      }).catch(() => {});
    }
  }, [id]);

  const loadTab = useCallback(async (key) => {
    if (!id || loaded[key]) return;
    try {
      if (key === 'photos') {
        const { data } = await propertyAPI.getImages(id);
        setImages(data);
      } else if (key === 'overview') {
        const { data } = await propertyAPI.getProTags(id);
        setProTags(data);
      } else if (key === 'documents') {
        const { data } = await propertyAPI.getDocuments(id);
        setDocuments(data);
      } else if (key === 'financials') {
        const { data } = await propertyAPI.getFinancials(id);
        setFinancials(data);
      } else if (key === 'access') {
        const { data } = await propertyAPI.listAccess(id);
        setAccessGrants(data);
      } else if (key === 'utilities') {
        const { data } = await propertyAPI.getUtilities(id);
        setUtilities(data);
      }
      setLoaded((prev) => ({ ...prev, [key]: true }));
    } catch (e) { console.error(e); }
  }, [id, loaded]);

  useEffect(() => { loadTab(tab); }, [tab, loadTab]);

  // Reload overview pro-tags after add/delete
  const reloadProTags = async () => {
    const { data } = await propertyAPI.getProTags(id);
    setProTags(data);
  };
  const reloadImages = async () => {
    const { data } = await propertyAPI.getImages(id);
    setImages(data);
  };
  const reloadDocuments = async () => {
    const { data } = await propertyAPI.getDocuments(id);
    setDocuments(data);
  };
  const reloadFinancials = async () => {
    const { data } = await propertyAPI.getFinancials(id);
    setFinancials(data);
  };
  const reloadAccess = async () => {
    const { data } = await propertyAPI.listAccess(id);
    setAccessGrants(data);
    setLoaded((prev) => ({ ...prev, access: true }));
  };
  const reloadUtilities = async () => {
    const { data } = await propertyAPI.getUtilities(id);
    setUtilities(data);
    setLoaded((prev) => ({ ...prev, utilities: true }));
  };

  if (propLoading) return <div style={{ padding: '60px 32px' }}><Spinner /></div>;
  if (propError || !property) {
    return (
      <div style={{ padding: '28px 32px' }}>
        <button className="btn ghost sm" onClick={() => navigate('/dashboard')} style={{ marginBottom: 20 }}>← All Properties</button>
        <EmptyState message="Property not found." />
      </div>
    );
  }

  const typeLabel = PROP_TYPES.find((t) => t.value === property.property_type)?.label || property.property_type;

  // Financial summary
  const totalIncome = financials.filter((f) => f.type === 'income').reduce((s, f) => s + f.amount, 0);
  const totalExpenses = financials.filter((f) => f.type === 'expense').reduce((s, f) => s + f.amount, 0);

  return (
    <div className="page-wrap" style={{ paddingTop: 24 }}>
      {/* Yellow strip while the property is in its 24h grace window. */}
      <PendingDeletionBanner property={property} onReinstate={() => refetchProp()} />

      {/* ── Back ── */}
      <button className="btn ghost sm" onClick={() => navigate('/dashboard')} style={{ marginBottom: 16, gap: 6 }}>
        ← All Properties
      </button>

      {/* ── Property header ── */}
      <div className="panel" style={{ padding: '22px 26px', marginBottom: 4 }}>
        <div className="prop-header-flex">

          {/* Left: image strip */}
          {images.length > 0 && (
            <div className="prop-header-img">
              <img
                src={`${API_BASE}${images.find(i => i.is_cover)?.url || images[0].url}`}
                alt="Property cover"
                style={{ width: '100%', height: 148, objectFit: 'cover', borderRadius: 8, marginBottom: 6, display: 'block' }}
                onError={(e) => { e.target.style.display = 'none'; }}
              />
              {images.length > 1 && (
                <div style={{ display: 'flex', gap: 5 }}>
                  {images.slice(0, 4).filter(i => !i.is_cover).slice(0, 3).map((img) => (
                    <img key={img.id}
                      src={`${API_BASE}${img.url}`}
                      alt=""
                      style={{ flex: 1, height: 60, objectFit: 'cover', borderRadius: 5, minWidth: 0 }}
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  ))}
                  {images.length > 4 && (
                    <div style={{
                      flex: 1, height: 60, borderRadius: 5, background: 'rgba(0,0,0,0.55)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer',
                    }} onClick={() => setTab('photos')}>
                      +{images.length - 4}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Right: details */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4, flexWrap: 'wrap' }}>
              <div className="mono" style={{ fontSize: 10, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'var(--ink-4)' }}>
                {typeLabel}{property.style ? ` · ${property.style}` : ''}
              </div>
              {property.occupancy_status && <OccupancyBadge status={property.occupancy_status} />}
            </div>
            <h1 className="serif" style={{ fontSize: 26, margin: '0 0 2px', color: 'var(--forest)', fontWeight: 600, lineHeight: 1.15 }}>
              {property.address_line1}
              {property.address_line2 && <span style={{ fontSize: 17, fontWeight: 400, color: 'var(--ink-3)' }}> {property.address_line2}</span>}
            </h1>
            <div style={{ fontSize: 14, color: 'var(--ink-3)', marginBottom: 14 }}>{fmtAddress(property)}</div>

            {/* Stats row */}
            <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', alignItems: 'center', marginBottom: 14 }}>
              {property.condition_grade && <GradePill grade={property.condition_grade} size="lg" />}
              {property.bedrooms != null && <Stat label="Beds" value={property.bedrooms} />}
              {property.bathrooms != null && <Stat label="Baths" value={property.bathrooms} />}
              {property.sqft ? <Stat label={`Size (${property.country === 'AU' ? 'sqm' : 'sq ft'})`} value={property.sqft.toLocaleString()} /> : null}
              {property.year_built ? <Stat label="Built" value={property.year_built} /> : null}
              {property.lot_size ? <Stat label="Lot" value={property.lot_size} /> : null}
              <div style={{ width: 1, background: 'var(--hairline)', alignSelf: 'stretch' }} />
              <Stat label="Records" value={total} />
            </div>

            {/* Top features */}
            {(property.features || []).length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {(property.features || []).slice(0, 5).map((feat) => (
                  <span key={feat} style={{
                    fontSize: 11, padding: '3px 9px', borderRadius: 12, fontWeight: 500,
                    background: 'rgba(31,58,46,0.08)', color: 'var(--forest)',
                    border: '1px solid rgba(31,58,46,0.15)',
                  }}>{feat}</span>
                ))}
                {(property.features || []).length > 5 && (
                  <span style={{ fontSize: 11, padding: '3px 9px', color: 'var(--ink-4)' }}>
                    +{(property.features || []).length - 5} more
                  </span>
                )}
              </div>
            )}
          </div>

          {canWrite && (
            <div style={{ display: 'flex', gap: 8, flexShrink: 0, alignItems: 'flex-start' }}>
              <button className="btn ghost sm" onClick={() => setEditModal(true)}>
                ✎ Edit
              </button>
              {/* Delete / Transfer are owner-only and live in a kebab so
                  they don't compete visually with the primary Edit CTA. */}
              {isOwner && (
                <PropertyDangerMenu
                  property={property}
                  onAfterDelete={() => refetchProp()}
                  onAfterTransfer={() => refetchProp()}
                />
              )}
            </div>
          )}
        </div>
      </div>

      {/* ── Tabs ── */}
      <div style={{ marginTop: 20 }}>
        <TabBar
          active={tab}
          onChange={setTab}
          tabs={[
            { key: 'overview', label: 'Overview' },
            { key: 'redbook', label: `Redbook (${total})` },
            { key: 'photos', label: `Photos (${images.length})` },
            { key: 'documents', label: `Documents (${documents.length})` },
            ...(canViewFinancials ? [{ key: 'financials', label: 'Financials' }] : []),
            ...(isOwner ? [{ key: 'utilities', label: 'Utilities' }] : []),
            ...(isOwner ? [{ key: 'access', label: `Access (${accessGrants.length})` }] : []),
            { key: 'calculator', label: 'Calculator' },
          ]}
        />

        {/* ── Overview tab ── */}
        {tab === 'overview' && (
          <div className="grid-2">
            {/* Features */}
            <div className="panel" style={{ padding: 20 }}>
              <div style={{ ...sectionLabel, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>Property Features</span>
                {canWrite && <span style={{ fontSize: 10, color: 'var(--ink-4)', fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>Click to toggle</span>}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 }}>
                {FEATURES.map((feat) => {
                  const active = (property.features || []).includes(feat);
                  return (
                    <label
                      key={feat}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 8, cursor: canWrite ? 'pointer' : 'default',
                        padding: '6px 8px', borderRadius: 6, fontSize: 13, color: active ? 'var(--forest)' : 'var(--ink-3)',
                        background: active ? 'rgba(31,58,46,0.06)' : 'transparent',
                        border: `1px solid ${active ? 'rgba(31,58,46,0.2)' : 'transparent'}`,
                      }}
                    >
                      {canWrite && (
                        <input
                          type="checkbox"
                          checked={active}
                          style={{ accentColor: 'var(--forest)', width: 14, height: 14 }}
                          onChange={async () => {
                            const current = property.features || [];
                            const next = active
                              ? current.filter((f) => f !== feat)
                              : [...current, feat];
                            await propertyAPI.update(id, { features: next });
                            refetchProp();
                          }}
                        />
                      )}
                      {!canWrite && <span style={{ fontSize: 14 }}>{active ? '✓' : '·'}</span>}
                      {feat}
                    </label>
                  );
                })}
              </div>
            </div>

            {/* Tagged professionals */}
            <div className="panel" style={{ padding: 20 }}>
              <div style={{ ...sectionLabel, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>Linked Professionals</span>
                {canWrite && (
                  <button className="btn ghost sm" style={{ fontSize: 10, textTransform: 'none', letterSpacing: 0 }}
                    onClick={() => setAddTagModal(true)}>
                    + Add
                  </button>
                )}
              </div>

              {proTags.length === 0 ? (
                <div style={{ color: 'var(--ink-4)', fontSize: 13, textAlign: 'center', padding: '20px 0' }}>
                  No professionals linked yet.
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {proTags.map((tag) => (
                    <div key={tag.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 12px', border: '1px solid var(--hairline)', borderRadius: 8 }}>
                      <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'var(--forest)', color: 'var(--paper)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, flexShrink: 0 }}>
                        {tag.name.split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase()}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink)' }}>{tag.name}</span>
                          {tag.pro_profile_id && (
                            <button className="btn ghost sm" style={{ fontSize: 10, padding: '1px 6px', height: 18 }}
                              onClick={() => navigate(`/pros/${tag.pro_profile_id}`)}>
                              View profile
                            </button>
                          )}
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--ink-4)' }}>{tag.role}{tag.company ? ` · ${tag.company}` : ''}</div>
                        {(tag.email || tag.phone) && (
                          <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 2 }}>
                            {tag.email && <span>{tag.email}</span>}
                            {tag.email && tag.phone && <span style={{ margin: '0 6px' }}>·</span>}
                            {tag.phone && <span>{tag.phone}</span>}
                          </div>
                        )}
                      </div>
                      {canWrite && (
                        <button
                          onClick={async () => {
                            await propertyAPI.deleteProTag(id, tag.id);
                            reloadProTags();
                          }}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-4)', fontSize: 16, padding: '0 2px', lineHeight: 1 }}
                        >
                          ×
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── Redbook tab ── */}
        {tab === 'redbook' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', flex: 1 }}>
                <input
                  placeholder="Search records…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={{ ...fieldStyle, width: 280, height: 34 }}
                />
                {['all', 'service', 'renovation', 'inspection', 'compliance'].map((f) => (
                  <Chip key={f} active={filter === f} onClick={() => setFilter(f)} style={{ height: 28, padding: '0 10px', cursor: 'pointer' }}>
                    {f === 'all' ? 'All' : TYPE_META[f]?.label || f}
                  </Chip>
                ))}
              </div>
              {canWrite && (
                <button className="btn primary" onClick={() => setShowAddEntry(true)}>+ Add entry</button>
              )}
            </div>
            {entriesLoading ? <Spinner /> : entries.length === 0
              ? <EmptyState message={search || filter !== 'all' ? 'No records match your filter.' : 'No history yet. Add the first entry.'} />
              : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {entries.map((entry) => (
                    <EntryCard
                      key={entry.id}
                      entry={entry}
                      propertyId={id}
                      canWrite={canWrite}
                      onEdit={(e) => setEditEntry(e)}
                      onDeleted={refetchTimeline}
                    />
                  ))}
                </div>
              )
            }
          </div>
        )}

        {/* ── Photos tab ── */}
        {tab === 'photos' && (
          <PhotoTab
            propertyId={id}
            images={images}
            canWrite={canWrite}
            onReload={reloadImages}
            coverUrl={property.cover_image_url}
            onPropertyUpdate={refetchProp}
          />
        )}

        {/* ── Documents tab ── */}
        {tab === 'documents' && (
          <DocumentTab
            propertyId={id}
            documents={documents}
            canWrite={canWrite}
            onReload={reloadDocuments}
          />
        )}

        {/* ── Financials tab ── */}
        {tab === 'financials' && (
          <div>
            {/* Summary cards */}
            <div className="grid-3" style={{ marginBottom: 24 }}>
              <SummaryCard label="Total Income" value={totalIncome} color="#15803d" />
              <SummaryCard label="Total Expenses" value={totalExpenses} color="#dc2626" />
              <SummaryCard label="Net" value={totalIncome - totalExpenses} color={totalIncome - totalExpenses >= 0 ? '#15803d' : '#dc2626'} />
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 14 }}>
              {canWrite && <button className="btn primary" onClick={() => setAddFinModal(true)}>+ Add entry</button>}
            </div>

            <FinancialTable
              financials={financials}
              canWrite={canWrite}
              onEdit={(entry) => setEditFinEntry(entry)}
              onDelete={async (entryId) => {
                await propertyAPI.deleteFinancial(id, entryId);
                reloadFinancials();
              }}
            />
          </div>
        )}

        {/* ── Utilities tab (owner only) ── */}
        {tab === 'utilities' && isOwner && (
          <UtilitiesTab
            propertyId={id}
            utilities={utilities}
            onReload={reloadUtilities}
          />
        )}

        {/* ── Access tab (owner only) ── */}
        {tab === 'access' && isOwner && (
          <AccessTab
            propertyId={id}
            grants={accessGrants}
            onReload={reloadAccess}
          />
        )}

        {/* ── Calculator tab ── */}
        {tab === 'calculator' && (
          <PropertyCalculatorTab property={property} />
        )}
      </div>

      {/* ── Modals ── */}
      {editModal && (
        <EditPropertyModal
          property={property}
          onClose={() => setEditModal(false)}
          onSaved={() => { setEditModal(false); refetchProp(); }}
        />
      )}
      {addTagModal && (
        <AddProTagModal
          propertyId={id}
          onClose={() => setAddTagModal(false)}
          onSaved={() => { setAddTagModal(false); reloadProTags(); }}
        />
      )}
      {addFinModal && (
        <FinancialModal
          propertyId={id}
          onClose={() => setAddFinModal(false)}
          onSaved={() => { setAddFinModal(false); reloadFinancials(); }}
        />
      )}
      {editFinEntry && (
        <FinancialModal
          propertyId={id}
          entry={editFinEntry}
          onClose={() => setEditFinEntry(null)}
          onSaved={() => { setEditFinEntry(null); reloadFinancials(); }}
        />
      )}
      {showAddEntry && (
        <EntryModal
          propertyId={id}
          onClose={() => setShowAddEntry(false)}
          onSaved={() => { setShowAddEntry(false); refetchTimeline(); }}
        />
      )}
      {editEntry && (
        <EntryModal
          propertyId={id}
          entry={editEntry}
          onClose={() => setEditEntry(null)}
          onSaved={() => { setEditEntry(null); refetchTimeline(); }}
        />
      )}
    </div>
  );
}

// ── Utilities Tab ─────────────────────────────────────────────────────────────
const UTILITY_CATEGORIES = [
  {
    type: 'electricity',
    label: 'Electricity',
    icon: '⚡',
    desc: 'Connect your electricity account',
    providers: [
      { name: 'AGL', desc: 'Australia\'s largest energy company', url: 'https://www.agl.com.au/residential/electricity' },
      { name: 'Origin Energy', desc: 'Competitive rates, green options', url: 'https://www.originenergy.com.au/electricity' },
      { name: 'EnergyAustralia', desc: 'Flexible plans for homes', url: 'https://www.energyaustralia.com.au/home/electricity-and-gas' },
      { name: 'Red Energy', desc: 'Owned by Snowy Hydro, great value', url: 'https://www.redenergy.com.au' },
      { name: 'Alinta Energy', desc: 'No-frills competitive pricing', url: 'https://www.alinta.net.au' },
      { name: 'Simply Energy', desc: 'Simple plans, no lock-in', url: 'https://www.simplyenergy.com.au' },
      { name: 'Amber Electric', desc: 'Wholesale spot pricing for smart homes', url: 'https://www.amber.com.au' },
      { name: 'Dodo', desc: 'Budget-friendly electricity plans', url: 'https://www.dodo.com/electricity' },
    ],
  },
  {
    type: 'gas',
    label: 'Gas',
    icon: '🔥',
    desc: 'Set up your gas connection',
    providers: [
      { name: 'AGL', desc: 'Natural gas plans for homes', url: 'https://www.agl.com.au/residential/gas' },
      { name: 'Origin Energy', desc: 'Gas plans with bundle discounts', url: 'https://www.originenergy.com.au/gas' },
      { name: 'EnergyAustralia', desc: 'Flexible gas plans', url: 'https://www.energyaustralia.com.au/home/electricity-and-gas' },
      { name: 'Alinta Energy', desc: 'Competitive gas rates', url: 'https://www.alinta.net.au' },
      { name: 'Simply Energy', desc: 'Straightforward gas plans', url: 'https://www.simplyenergy.com.au' },
      { name: 'Kleenheat', desc: 'WA and NT gas specialist', url: 'https://www.kleenheat.com.au' },
    ],
  },
  {
    type: 'water',
    label: 'Water',
    icon: '💧',
    desc: 'Register with your regional water authority',
    providers: [
      { name: 'Sydney Water', desc: 'Greater Sydney region', url: 'https://www.sydneywater.com.au' },
      { name: 'Yarra Valley Water', desc: 'Melbourne\'s north and east', url: 'https://www.yvw.com.au' },
      { name: 'City West Water', desc: 'Melbourne\'s west and inner north', url: 'https://www.citywestwater.com.au' },
      { name: 'South East Water', desc: 'Melbourne\'s south-east', url: 'https://www.southeastwater.com.au' },
      { name: 'SA Water', desc: 'South Australia', url: 'https://www.sawater.com.au' },
      { name: 'Queensland Urban Utilities', desc: 'South-east Queensland', url: 'https://www.urbanutilities.com.au' },
      { name: 'Water Corporation', desc: 'Western Australia', url: 'https://www.watercorporation.com.au' },
      { name: 'Icon Water', desc: 'ACT and surrounds', url: 'https://www.iconwater.com.au' },
    ],
  },
  {
    type: 'internet',
    label: 'Internet / NBN',
    icon: '🌐',
    desc: 'Set up broadband for the property',
    providers: [
      { name: 'Telstra', desc: 'Australia\'s largest network', url: 'https://www.telstra.com.au/internet' },
      { name: 'Optus', desc: 'Competitive NBN plans', url: 'https://www.optus.com.au/broadband' },
      { name: 'Aussie Broadband', desc: 'Award-winning local ISP', url: 'https://www.aussiebroadband.com.au' },
      { name: 'TPG', desc: 'Great value NBN plans', url: 'https://www.tpg.com.au' },
      { name: 'iiNet', desc: 'Reliable NBN with local support', url: 'https://www.iinet.net.au' },
      { name: 'Superloop', desc: 'Fast and reliable broadband', url: 'https://www.superloop.com' },
      { name: 'Internode', desc: 'Premium service, great support', url: 'https://www.internode.on.net' },
      { name: 'Vodafone', desc: 'NBN + mobile bundles', url: 'https://www.vodafone.com.au/broadband' },
    ],
  },
  {
    type: 'insurance',
    label: 'Home Insurance',
    icon: '🛡',
    desc: 'Protect your property with home and contents insurance',
    providers: [
      { name: 'AAMI', desc: 'Popular home and contents cover', url: 'https://www.aami.com.au/home-insurance' },
      { name: 'Budget Direct', desc: 'Award-winning, affordable cover', url: 'https://www.budgetdirect.com.au/home-insurance' },
      { name: 'youi', desc: 'Tailored quotes based on your situation', url: 'https://www.youi.com.au/home-insurance' },
      { name: 'Allianz', desc: 'Comprehensive home insurance', url: 'https://www.allianz.com.au/home-insurance' },
      { name: 'Suncorp', desc: 'Home and contents bundles', url: 'https://www.suncorp.com.au/insurance/home' },
      { name: 'QBE', desc: 'Flexible home insurance options', url: 'https://www.qbe.com/au/insurance/home' },
      { name: 'NRMA Insurance', desc: 'NSW, QLD, ACT and TAS', url: 'https://www.nrma.com.au/home-insurance' },
      { name: 'RACV', desc: 'Victoria\'s leading insurer', url: 'https://www.racv.com.au/insurance/home-insurance' },
    ],
  },
];

function ProviderCard({ provider, isCurrent, onSelect }) {
  return (
    <div style={{
      border: `1.5px solid ${isCurrent ? 'var(--forest)' : 'var(--hairline)'}`,
      borderRadius: 10,
      padding: '12px 14px',
      background: isCurrent ? 'rgba(31,58,46,0.04)' : '#fff',
      display: 'flex',
      flexDirection: 'column',
      gap: 6,
      position: 'relative',
    }}>
      {isCurrent && (
        <div style={{
          position: 'absolute', top: 8, right: 8,
          fontSize: 10, fontWeight: 700, color: 'var(--forest)',
          background: 'rgba(31,58,46,0.12)', padding: '2px 7px', borderRadius: 8,
        }}>✓ Current</div>
      )}
      <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--ink)', paddingRight: isCurrent ? 64 : 0 }}>
        {provider.name}
      </div>
      <div style={{ fontSize: 12, color: 'var(--ink-4)', lineHeight: 1.4 }}>{provider.desc}</div>
      <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
        <button
          className="btn ghost sm"
          style={{ fontSize: 11, padding: '3px 10px' }}
          onClick={(e) => { e.stopPropagation(); onSelect(provider.name); }}
        >
          {isCurrent ? 'Update' : 'Set as provider'}
        </button>
        <a
          href={provider.url}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            fontSize: 11, padding: '3px 10px', borderRadius: 6,
            background: 'var(--forest)', color: '#fff', textDecoration: 'none',
            display: 'inline-flex', alignItems: 'center', gap: 4, fontWeight: 500,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          Open account ↗
        </a>
      </div>
    </div>
  );
}

function UtilitySection({ category, record, propertyId, onReload }) {
  const [expanded, setExpanded] = useState(false);
  const [accountNum, setAccountNum] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSelect = async (providerName) => {
    setSaving(true);
    try {
      await propertyAPI.upsertUtility(propertyId, category.type, {
        utility_type: category.type,
        provider_name: providerName,
        account_number: record?.account_number || accountNum || null,
        notes: record?.notes || notes || null,
        is_connected: true,
      });
      onReload();
    } catch { /* ignore */ } finally { setSaving(false); }
  };

  const handleClear = async () => {
    await propertyAPI.deleteUtility(propertyId, category.type);
    onReload();
  };

  const saveDetails = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await propertyAPI.upsertUtility(propertyId, category.type, {
        utility_type: category.type,
        provider_name: record?.provider_name || null,
        account_number: accountNum || null,
        notes: notes || null,
        is_connected: true,
      });
      setExpanded(false);
      onReload();
    } catch { /* ignore */ } finally { setSaving(false); }
  };

  return (
    <div className="panel" style={{ padding: '18px 20px', marginBottom: 16 }}>
      {/* Category header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
        <span style={{ fontSize: 22 }}>{category.icon}</span>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: 16, color: 'var(--forest)' }}>{category.label}</div>
          <div style={{ fontSize: 12, color: 'var(--ink-4)' }}>{category.desc}</div>
        </div>
        {record?.provider_name && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 11, color: 'var(--ink-4)' }}>Connected provider</div>
              <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--forest)' }}>{record.provider_name}</div>
              {record.account_number && (
                <div style={{ fontSize: 11, color: 'var(--ink-4)' }}>Acct: {record.account_number}</div>
              )}
            </div>
            <button
              className="btn ghost sm"
              style={{ fontSize: 11, color: '#dc2626' }}
              onClick={handleClear}
            >Remove</button>
            <button
              className="btn ghost sm"
              style={{ fontSize: 11 }}
              onClick={() => {
                setAccountNum(record.account_number || '');
                setNotes(record.notes || '');
                setExpanded(!expanded);
              }}
            >Edit details</button>
          </div>
        )}
      </div>

      {/* Account details form (when editing) */}
      {expanded && (
        <form onSubmit={saveDetails} style={{
          background: 'rgba(31,58,46,0.03)', border: '1px solid var(--hairline)',
          borderRadius: 8, padding: 14, marginBottom: 14,
          display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, alignItems: 'end',
        }}>
          <div>
            <label style={{ fontSize: 12, fontWeight: 500, display: 'block', marginBottom: 4 }}>Account number</label>
            <input className="input" value={accountNum} onChange={(e) => setAccountNum(e.target.value)}
              placeholder="Optional" style={{ width: '100%' }} />
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 500, display: 'block', marginBottom: 4 }}>Notes</label>
            <input className="input" value={notes} onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. billing date, plan name" style={{ width: '100%' }} />
          </div>
          <div style={{ gridColumn: '1 / -1', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button type="button" className="btn ghost sm" onClick={() => setExpanded(false)}>Cancel</button>
            <button type="submit" className="btn primary sm" disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
          </div>
        </form>
      )}

      {/* Provider grid */}
      <div className="grid-fill-sm">
        {category.providers.map((provider) => (
          <ProviderCard
            key={provider.name}
            provider={provider}
            isCurrent={record?.provider_name === provider.name}
            onSelect={handleSelect}
          />
        ))}
      </div>
    </div>
  );
}

function UtilitiesTab({ propertyId, utilities, onReload }) {
  const getRecord = (type) => utilities.find((u) => u.utility_type === type);
  const connectedCount = utilities.filter((u) => u.provider_name).length;

  return (
    <div style={{ paddingTop: 20 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 20 }}>
        <div>
          <div style={{ fontSize: 13, color: 'var(--ink-4)' }}>
            Track and connect your property's utility accounts. Click "Open account" to sign up with a provider, or "Set as provider" to record your existing connection.
          </div>
        </div>
        {connectedCount > 0 && (
          <span style={{
            flexShrink: 0, fontSize: 12, padding: '2px 10px', borderRadius: 10,
            background: 'rgba(21,128,61,0.1)', color: '#15803d', fontWeight: 600,
          }}>
            {connectedCount} of {UTILITY_CATEGORIES.length} connected
          </span>
        )}
      </div>
      {UTILITY_CATEGORIES.map((cat) => (
        <UtilitySection
          key={cat.type}
          category={cat}
          record={getRecord(cat.type)}
          propertyId={propertyId}
          onReload={onReload}
        />
      ))}
    </div>
  );
}

// ── Access Tab ────────────────────────────────────────────────────────────────
const PERM_LABELS = {
  can_view_financials: 'View financials',
  can_manage_tenancy: 'Manage tenancy',
  can_manage_maintenance: 'Manage maintenance',
  can_upload_documents: 'Upload documents',
  can_book_professionals: 'Book professionals',
};
const PERM_DEFAULTS = {
  can_view_financials: false,
  can_manage_tenancy: true,
  can_manage_maintenance: true,
  can_upload_documents: true,
  can_book_professionals: true,
};
const ACCESS_LEVEL_OPTS = ['viewer', 'editor', 'admin'];

function PermBadge({ label, active }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px',
      borderRadius: 12, fontSize: 11, fontWeight: 500,
      background: active ? 'rgba(21,128,61,0.1)' : 'rgba(0,0,0,0.06)',
      color: active ? '#15803d' : 'var(--ink-4)',
      border: `1px solid ${active ? 'rgba(21,128,61,0.25)' : 'transparent'}`,
    }}>
      {active ? '✓' : '–'} {label}
    </span>
  );
}

function GrantCard({ grant, propertyId, onUpdated }) {
  const [expanded, setExpanded] = useState(false);
  const [perms, setPerms] = useState(null);
  const [saving, setSaving] = useState(false);

  const startEdit = () => {
    setPerms({
      role_label: grant.role_label || '',
      notes: grant.notes || '',
      access_level: grant.access_level,
      can_view_financials: grant.can_view_financials,
      can_manage_tenancy: grant.can_manage_tenancy,
      can_manage_maintenance: grant.can_manage_maintenance,
      can_upload_documents: grant.can_upload_documents,
      can_book_professionals: grant.can_book_professionals,
    });
    setExpanded(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      await propertyAPI.updateAccess(propertyId, grant.id, perms);
      setExpanded(false);
      setPerms(null);
      onUpdated();
    } catch { /* ignore */ } finally { setSaving(false); }
  };

  const revoke = async () => {
    if (!window.confirm(`Revoke access for ${grant.grantee_name || grant.grantee_email}?`)) return;
    await propertyAPI.revokeAccess(propertyId, grant.id);
    onUpdated();
  };

  return (
    <div className="panel" style={{ padding: 16, marginBottom: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{
          width: 36, height: 36, borderRadius: 18, background: 'var(--forest)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: '#fff', fontWeight: 700, fontSize: 14, flexShrink: 0,
        }}>
          {(grant.grantee_name || grant.grantee_email || '?')[0].toUpperCase()}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: 14 }}>
            {grant.grantee_name || <span style={{ color: 'var(--ink-4)' }}>Unknown</span>}
            <span style={{
              marginLeft: 8, fontSize: 11, padding: '1px 7px', borderRadius: 10,
              background: 'rgba(31,58,46,0.1)', color: 'var(--forest)', fontWeight: 600,
            }}>{grant.access_level}</span>
          </div>
          <div style={{ fontSize: 12, color: 'var(--ink-4)' }}>{grant.grantee_email}</div>
          {grant.role_label && <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 2 }}>{grant.role_label}</div>}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn ghost sm" onClick={startEdit}>Edit</button>
          <button className="btn ghost sm" style={{ color: '#dc2626' }} onClick={revoke}>Revoke</button>
        </div>
      </div>

      {/* Permission badges summary */}
      {!expanded && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 12 }}>
          {Object.keys(PERM_LABELS).map((k) => (
            <PermBadge key={k} label={PERM_LABELS[k]} active={grant[k]} />
          ))}
        </div>
      )}

      {/* Edit form */}
      {expanded && perms && (
        <div style={{ marginTop: 14, borderTop: '1px solid var(--hairline)', paddingTop: 14 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
            <div>
              <label style={{ fontSize: 12, fontWeight: 500, display: 'block', marginBottom: 4 }}>Role label</label>
              <input className="input" value={perms.role_label} onChange={(e) => setPerms({ ...perms, role_label: e.target.value })}
                placeholder="e.g. Real Estate Agent" style={{ width: '100%' }} />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 500, display: 'block', marginBottom: 4 }}>Access level</label>
              <select className="input" value={perms.access_level} onChange={(e) => setPerms({ ...perms, access_level: e.target.value })}
                style={{ width: '100%' }}>
                {ACCESS_LEVEL_OPTS.map((l) => <option key={l} value={l}>{l}</option>)}
              </select>
            </div>
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: 12, fontWeight: 500, display: 'block', marginBottom: 4 }}>Notes</label>
            <textarea className="input" value={perms.notes} onChange={(e) => setPerms({ ...perms, notes: e.target.value })}
              rows={2} placeholder="Optional instructions for this person" style={{ width: '100%', resize: 'vertical' }} />
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 14 }}>
            {Object.keys(PERM_LABELS).map((k) => (
              <label key={k} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
                <input type="checkbox" checked={perms[k]} onChange={(e) => setPerms({ ...perms, [k]: e.target.checked })}
                  style={{ accentColor: 'var(--forest)', width: 14, height: 14 }} />
                {PERM_LABELS[k]}
              </label>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button className="btn ghost sm" onClick={() => { setExpanded(false); setPerms(null); }}>Cancel</button>
            <button className="btn primary sm" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
          </div>
        </div>
      )}
    </div>
  );
}

function GrantForm({ propertyId, onGranted }) {
  const [email, setEmail] = useState('');
  const [roleLabel, setRoleLabel] = useState('');
  const [level, setLevel] = useState('editor');
  const [expiryDays, setExpiryDays] = useState('');
  const [notes, setNotes] = useState('');
  const [perms, setPerms] = useState({ ...PERM_DEFAULTS });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    setLoading(true);
    try {
      await propertyAPI.grantAccess(propertyId, {
        user_email: email,
        access_level: level,
        role_label: roleLabel || null,
        notes: notes || null,
        expires_in_days: expiryDays ? parseInt(expiryDays) : null,
        ...perms,
      });
      setEmail(''); setRoleLabel(''); setLevel('editor'); setExpiryDays(''); setNotes('');
      setPerms({ ...PERM_DEFAULTS });
      onGranted();
    } catch (e) {
      setErr(e.response?.data?.detail || 'Failed to grant access');
    } finally { setLoading(false); }
  };

  return (
    <form onSubmit={submit} style={{ background: 'rgba(31,58,46,0.03)', border: '1px solid var(--hairline)', borderRadius: 8, padding: 18, marginBottom: 24 }}>
      <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 14, color: 'var(--forest)' }}>Grant Access</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 12 }}>
        <div>
          <label style={{ fontSize: 12, fontWeight: 500, display: 'block', marginBottom: 4 }}>Email *</label>
          <input className="input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
            placeholder="user@example.com" style={{ width: '100%' }} />
        </div>
        <div>
          <label style={{ fontSize: 12, fontWeight: 500, display: 'block', marginBottom: 4 }}>Role label</label>
          <input className="input" value={roleLabel} onChange={(e) => setRoleLabel(e.target.value)}
            placeholder="e.g. Real Estate Agent" style={{ width: '100%' }} />
        </div>
        <div>
          <label style={{ fontSize: 12, fontWeight: 500, display: 'block', marginBottom: 4 }}>Access level</label>
          <select className="input" value={level} onChange={(e) => setLevel(e.target.value)} style={{ width: '100%' }}>
            {ACCESS_LEVEL_OPTS.map((l) => <option key={l} value={l}>{l}</option>)}
          </select>
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
        <div>
          <label style={{ fontSize: 12, fontWeight: 500, display: 'block', marginBottom: 4 }}>Notes</label>
          <textarea className="input" value={notes} onChange={(e) => setNotes(e.target.value)}
            rows={2} placeholder="Optional instructions" style={{ width: '100%', resize: 'vertical' }} />
        </div>
        <div>
          <label style={{ fontSize: 12, fontWeight: 500, display: 'block', marginBottom: 4 }}>Expires in (days)</label>
          <input className="input" type="number" value={expiryDays} onChange={(e) => setExpiryDays(e.target.value)}
            placeholder="Leave blank = no expiry" style={{ width: '100%' }} min={1} max={365} />
        </div>
      </div>
      <div style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 8 }}>Permissions</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
          {Object.keys(PERM_LABELS).map((k) => (
            <label key={k} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
              <input type="checkbox" checked={perms[k]} onChange={(e) => setPerms({ ...perms, [k]: e.target.checked })}
                style={{ accentColor: 'var(--forest)', width: 14, height: 14 }} />
              {PERM_LABELS[k]}
            </label>
          ))}
        </div>
      </div>
      {err && <div style={{ color: '#dc2626', fontSize: 13, marginBottom: 10 }}>{err}</div>}
      <button type="submit" className="btn primary" disabled={loading}>{loading ? 'Granting…' : '+ Grant Access'}</button>
    </form>
  );
}

function AccessTab({ propertyId, grants, onReload }) {
  return (
    <div style={{ paddingTop: 20 }}>
      <GrantForm propertyId={propertyId} onGranted={onReload} />

      {grants.length === 0 ? (
        <EmptyState message="No access grants yet. Use the form above to give an agent, manager, or other person access." />
      ) : (
        <div>
          <div style={{ fontSize: 12, color: 'var(--ink-4)', marginBottom: 12 }}>{grants.length} active grant{grants.length !== 1 ? 's' : ''}</div>
          {grants.map((g) => (
            <GrantCard key={g.id} grant={g} propertyId={propertyId} onUpdated={onReload} />
          ))}
        </div>
      )}
    </div>
  );
}

// ── Summary card ─────────────────────────────────────────────────────────────
function SummaryCard({ label, value, color }) {
  return (
    <div className="panel" style={{ padding: '16px 20px' }}>
      <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--ink-4)', marginBottom: 6 }}>{label}</div>
      <div className="serif tnum" style={{ fontSize: 26, fontWeight: 700, color }}>
        {value < 0 ? '-' : ''}{Math.abs(value).toLocaleString('en-AU', { style: 'currency', currency: 'AUD', maximumFractionDigits: 0 })}
      </div>
    </div>
  );
}

// ── Stat block ────────────────────────────────────────────────────────────────
function Stat({ label, value, accent }) {
  return (
    <div>
      <div style={{ fontSize: 10, letterSpacing: '0.12em', textTransform: 'uppercase', color: accent ? 'var(--forest)' : 'var(--ink-4)' }}>{label}</div>
      <div className="serif tnum" style={{ fontSize: 20, fontWeight: 600, marginTop: 2, color: accent ? 'var(--forest)' : 'var(--ink)' }}>{value}</div>
    </div>
  );
}

// ── Photos tab ────────────────────────────────────────────────────────────────
function PhotoTab({ propertyId, images, canWrite, onReload, coverUrl, onPropertyUpdate }) {
  const fileRef = useRef();
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  async function upload(e) {
    const file = e.target.files[0];
    if (!file) return;
    const sizeErr = validateUploadFile(file);
    if (sizeErr) { setError(sizeErr); e.target.value = ''; return; }
    setUploading(true);
    setError('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('is_cover', images.length === 0 ? 'true' : 'false');
      await propertyAPI.uploadImage(propertyId, fd);
      await onReload();
      if (images.length === 0) onPropertyUpdate();
    } catch (err) {
      setError(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  }

  return (
    <div>
      {canWrite && (
        <div style={{ marginBottom: 16, display: 'flex', gap: 10, alignItems: 'center' }}>
          <button className="btn primary" onClick={() => fileRef.current?.click()} disabled={uploading}>
            {uploading ? 'Uploading…' : '+ Upload photo'}
          </button>
          <input ref={fileRef} type="file" accept={ACCEPT_IMAGES} style={{ display: 'none' }} onChange={upload} />
          {error && <span style={{ fontSize: 12, color: '#dc2626' }}>{error}</span>}
        </div>
      )}

      {images.length === 0 ? (
        <EmptyState message="No photos yet. Upload the first photo to start the gallery." />
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 14 }}>
          {images.map((img) => (
            <div key={img.id} style={{ borderRadius: 10, overflow: 'hidden', border: '1px solid var(--hairline)', position: 'relative' }}>
              <img
                src={`${API_BASE}${img.url}`}
                alt={img.caption || 'Property photo'}
                style={{ width: '100%', height: 160, objectFit: 'cover', display: 'block' }}
                onError={(e) => { e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="1" height="1"/>'; }}
              />
              {img.is_cover && (
                <span style={{ position: 'absolute', top: 8, left: 8, background: 'var(--forest)', color: '#f5f1e8', fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 4 }}>
                  COVER
                </span>
              )}
              <div style={{ padding: '8px 10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ fontSize: 12, color: 'var(--ink-4)' }}>{img.caption || ''}</span>
                {canWrite && (
                  <button
                    onClick={async () => {
                      await propertyAPI.deleteImage(propertyId, img.id);
                      await onReload();
                      if (img.is_cover) onPropertyUpdate();
                    }}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626', fontSize: 12, padding: 0 }}
                  >
                    Remove
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Documents tab ─────────────────────────────────────────────────────────────
function DocumentTab({ propertyId, documents, canWrite, onReload }) {
  const fileRef = useRef();
  const [uploading, setUploading] = useState(false);
  const [docType, setDocType] = useState('other');
  const [description, setDescription] = useState('');
  const [error, setError] = useState('');

  async function upload(e) {
    const file = e.target.files[0];
    if (!file) return;
    const sizeErr = validateUploadFile(file);
    if (sizeErr) { setError(sizeErr); e.target.value = ''; return; }
    setUploading(true);
    setError('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('document_type', docType);
      if (description.trim()) fd.append('description', description.trim());
      await propertyAPI.uploadDocument(propertyId, fd);
      await onReload();
      setDescription('');
    } catch (err) {
      setError(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  }

  return (
    <div>
      {canWrite && (
        <div className="panel" style={{ padding: 16, marginBottom: 20 }}>
          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div style={{ flex: '0 0 180px' }}>
              <div style={{ fontSize: 11, color: 'var(--ink-4)', marginBottom: 4 }}>Document type</div>
              <select value={docType} onChange={(e) => setDocType(e.target.value)} style={fieldStyle}>
                {DOC_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div style={{ flex: 1, minWidth: 180 }}>
              <div style={{ fontSize: 11, color: 'var(--ink-4)', marginBottom: 4 }}>Description (optional)</div>
              <input
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="e.g. Building insurance policy 2024"
                style={fieldStyle}
              />
            </div>
            <button className="btn primary" onClick={() => fileRef.current?.click()} disabled={uploading}>
              {uploading ? 'Uploading…' : '+ Upload document'}
            </button>
            <input ref={fileRef} type="file" accept={ACCEPT_DOCS} style={{ display: 'none' }} onChange={upload} />
          </div>
          {error && <div style={{ fontSize: 12, color: '#dc2626', marginTop: 8 }}>{error}</div>}
          <div style={{ fontSize: 11, color: 'var(--ink-4)', marginTop: 6 }}>
            JPG, PNG, PDF, DOC, DOCX, PPT, PPTX, XLS, XLSX &middot; max {MAX_UPLOAD_LABEL}
          </div>
        </div>
      )}

      {documents.length === 0 ? (
        <EmptyState message="No documents yet. Upload contracts, title deeds, insurance policies and more." />
      ) : (
        <div className="panel table-scroll" style={{ padding: 0 }}>
          <table style={{ width: '100%', minWidth: 580, borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f9fafb' }}>
                {['Type', 'Filename', 'Description', 'Size', 'Date', ''].map((h) => (
                  <th key={h} style={{ padding: '10px 14px', fontSize: 11, fontWeight: 700, color: 'var(--ink-4)', textAlign: 'left', borderBottom: '1px solid var(--hairline)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {documents.map((doc) => (
                <tr key={doc.id} style={{ borderBottom: '1px solid var(--hairline)' }}>
                  <td style={{ padding: '10px 14px', fontSize: 12 }}>
                    <Chip style={{ fontSize: 10, height: 20, padding: '0 7px' }}>
                      {DOC_TYPES.find((t) => t.value === doc.document_type)?.label || doc.document_type}
                    </Chip>
                  </td>
                  <td style={{ padding: '10px 14px' }}>
                    <a
                      href={`${API_BASE}${doc.file_url}`}
                      target="_blank"
                      rel="noreferrer"
                      style={{ fontSize: 13, color: 'var(--forest)', textDecoration: 'none', fontWeight: 500 }}
                    >
                      {doc.original_filename}
                    </a>
                  </td>
                  <td style={{ padding: '10px 14px', fontSize: 12, color: 'var(--ink-4)' }}>{doc.description || '—'}</td>
                  <td style={{ padding: '10px 14px', fontSize: 12, color: 'var(--ink-4)' }}>
                    {doc.file_size ? `${(doc.file_size / 1024).toFixed(0)} KB` : '—'}
                  </td>
                  <td style={{ padding: '10px 14px', fontSize: 12, color: 'var(--ink-4)' }}>
                    {new Date(doc.created_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </td>
                  <td style={{ padding: '10px 14px', textAlign: 'right' }}>
                    {canWrite && (
                      <button
                        onClick={async () => { await propertyAPI.deleteDocument(propertyId, doc.id); onReload(); }}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626', fontSize: 12 }}
                      >
                        Remove
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Financial table ───────────────────────────────────────────────────────────
function FinancialTable({ financials, canWrite, onEdit, onDelete }) {
  if (financials.length === 0) {
    return <EmptyState message="No financial entries yet. Track income and expenses for this property." />;
  }
  return (
    <div className="panel table-scroll" style={{ padding: 0 }}>
      <table style={{ width: '100%', minWidth: 640, borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ background: '#f9fafb' }}>
            {['Date', 'Type', 'Category', 'Description', 'Invoice #', 'GST', 'Amount', ''].map((h) => (
              <th key={h} style={{ padding: '10px 12px', fontSize: 11, fontWeight: 700, color: 'var(--ink-4)', textAlign: 'left', borderBottom: '1px solid var(--hairline)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {financials.map((f) => (
            <tr key={f.id} style={{ borderBottom: '1px solid var(--hairline)' }}>
              <td style={{ padding: '10px 12px', fontSize: 12, whiteSpace: 'nowrap' }}>{fmtDate(f.entry_date, { short: true })}</td>
              <td style={{ padding: '10px 12px' }}>
                <Chip variant={f.type === 'income' ? 'forest' : ''} style={{ fontSize: 10, height: 20, padding: '0 7px', background: f.type === 'income' ? '#dcfce7' : '#fee2e2', color: f.type === 'income' ? '#15803d' : '#dc2626', border: 'none' }}>
                  {f.type === 'income' ? '↑ Income' : '↓ Expense'}
                </Chip>
              </td>
              <td style={{ padding: '10px 12px', fontSize: 12, color: 'var(--ink-3)' }}>{f.category || '—'}</td>
              <td style={{ padding: '10px 12px', fontSize: 13, color: 'var(--ink)', maxWidth: 200 }}>
                <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.description || '—'}</div>
              </td>
              <td style={{ padding: '10px 12px', fontSize: 12, color: 'var(--ink-4)', fontFamily: 'monospace' }}>{f.invoice_number || '—'}</td>
              <td style={{ padding: '10px 12px', fontSize: 12, color: 'var(--ink-4)', textAlign: 'right' }}>
                {f.gst_amount != null ? `$${f.gst_amount.toLocaleString()}` : '—'}
              </td>
              <td style={{ padding: '10px 12px', fontSize: 13, fontWeight: 700, textAlign: 'right', whiteSpace: 'nowrap', color: f.type === 'income' ? '#15803d' : '#dc2626' }}>
                {f.type === 'income' ? '+' : '-'}${Math.abs(f.amount).toLocaleString()}
              </td>
              <td style={{ padding: '10px 12px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                {canWrite && (
                  <span style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                    <button onClick={() => onEdit(f)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-4)', fontSize: 12 }}>Edit</button>
                    <button onClick={() => onDelete(f.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626', fontSize: 12 }}>Del</button>
                  </span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Edit property modal ───────────────────────────────────────────────────────
function EditPropertyModal({ property, onClose, onSaved }) {
  const [form, setForm] = useState({
    address_line1: property.address_line1 || '',
    address_line2: property.address_line2 || '',
    city: property.city || '',
    state: property.state || '',
    zip_code: property.zip_code || '',
    property_type: property.property_type || 'single_family',
    style: property.style || '',
    year_built: property.year_built || '',
    bedrooms: property.bedrooms || '',
    bathrooms: property.bathrooms || '',
    sqft: property.sqft || '',
    lot_size: property.lot_size || '',
    occupancy_status: property.occupancy_status || '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const isAU = property.country === 'AU';

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  async function save(e) {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      await propertyAPI.update(property.id, {
        ...form,
        year_built: form.year_built ? parseInt(form.year_built) : undefined,
        bedrooms: form.bedrooms ? parseInt(form.bedrooms) : undefined,
        bathrooms: form.bathrooms ? parseFloat(form.bathrooms) : undefined,
        sqft: form.sqft ? parseInt(form.sqft) : undefined,
        style: form.style || undefined,
        address_line2: form.address_line2 || undefined,
        lot_size: form.lot_size || undefined,
        occupancy_status: form.occupancy_status || null,
      });
      onSaved();
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not save');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 620 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <h2 className="serif" style={{ fontSize: 20, margin: 0, color: 'var(--forest)', fontWeight: 600 }}>Edit Property</h2>
          <button onClick={onClose} className="btn ghost sm">✕</button>
        </div>
        <form onSubmit={save}>
          <div className="modal-body" style={{ display: 'grid', gap: 14 }}>
            {error && <div style={{ color: '#dc2626', fontSize: 13, padding: '6px 10px', background: '#fef2f2', borderRadius: 4 }}>{error}</div>}

            <div style={sectionLabel}>Address</div>
            <div className="field">
              <label>Street address</label>
              <input value={form.address_line1} onChange={set('address_line1')} required style={fieldStyle} />
            </div>
            <div className="field">
              <label>Unit / Suite (optional)</label>
              <input value={form.address_line2} onChange={set('address_line2')} style={fieldStyle} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
              <div className="field">
                <label>{isAU ? 'Suburb' : 'City'}</label>
                <input value={form.city} onChange={set('city')} required style={fieldStyle} />
              </div>
              <div className="field">
                <label>State</label>
                {isAU ? (
                  <select value={form.state} onChange={set('state')} style={fieldStyle}>
                    {AU_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                ) : (
                  <input value={form.state} onChange={set('state')} maxLength={2} style={{ ...fieldStyle, textTransform: 'uppercase' }} />
                )}
              </div>
              <div className="field">
                <label>{isAU ? 'Postcode' : 'ZIP Code'}</label>
                <input value={form.zip_code} onChange={set('zip_code')} maxLength={isAU ? 4 : 10} style={fieldStyle} />
              </div>
            </div>

            <div style={sectionLabel}>Property Details</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
              <div className="field">
                <label>Property type</label>
                <select value={form.property_type} onChange={set('property_type')} style={fieldStyle}>
                  {PROP_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
              <div className="field">
                <label>Style</label>
                <input value={form.style} onChange={set('style')} placeholder="e.g. Colonial, Queenslander" style={fieldStyle} />
              </div>
              <div className="field">
                <label>Occupancy status</label>
                <select value={form.occupancy_status} onChange={set('occupancy_status')} style={fieldStyle}>
                  {OCCUPANCY_STATUS_OPTS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10 }}>
              <div className="field"><label>Built</label><input type="number" value={form.year_built} onChange={set('year_built')} min={1700} max={2030} style={fieldStyle} /></div>
              <div className="field"><label>Beds</label><input type="number" value={form.bedrooms} onChange={set('bedrooms')} min={0} style={fieldStyle} /></div>
              <div className="field"><label>Baths</label><input type="number" value={form.bathrooms} onChange={set('bathrooms')} min={0} step={0.5} style={fieldStyle} /></div>
              <div className="field"><label>{isAU ? 'Size (sqm)' : 'Size (sq ft)'}</label><input type="number" value={form.sqft} onChange={set('sqft')} min={0} style={fieldStyle} /></div>
            </div>
            <div className="field"><label>Lot size</label><input value={form.lot_size} onChange={set('lot_size')} placeholder={isAU ? 'e.g. 650 sqm' : 'e.g. 0.25 acres'} style={fieldStyle} /></div>
          </div>
          <div className="modal-foot">
            <button type="button" className="btn ghost" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn primary" disabled={saving}>{saving ? 'Saving…' : '✓ Save changes'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Add pro tag modal ─────────────────────────────────────────────────────────
function AddProTagModal({ propertyId, onClose, onSaved }) {
  const [form, setForm] = useState({ role: 'Real Estate Agent', name: '', company: '', email: '', phone: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  async function save(e) {
    e.preventDefault();
    if (!form.name.trim()) { setError('Name is required'); return; }
    setSaving(true);
    setError('');
    try {
      await propertyAPI.addProTag(propertyId, {
        role: form.role,
        name: form.name.trim(),
        company: form.company.trim() || undefined,
        email: form.email.trim() || undefined,
        phone: form.phone.trim() || undefined,
      });
      onSaved();
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not save');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 480 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <h2 className="serif" style={{ fontSize: 20, margin: 0, color: 'var(--forest)', fontWeight: 600 }}>Link a Professional</h2>
          <button onClick={onClose} className="btn ghost sm">✕</button>
        </div>
        <form onSubmit={save}>
          <div className="modal-body" style={{ display: 'grid', gap: 12 }}>
            {error && <div style={{ color: '#dc2626', fontSize: 13 }}>{error}</div>}
            <div className="field">
              <label>Role</label>
              <select value={form.role} onChange={set('role')} style={fieldStyle}>
                {PRO_ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div className="field"><label>Name *</label><input value={form.name} onChange={set('name')} required style={fieldStyle} /></div>
              <div className="field"><label>Company</label><input value={form.company} onChange={set('company')} style={fieldStyle} /></div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div className="field"><label>Email</label><input type="email" value={form.email} onChange={set('email')} style={fieldStyle} /></div>
              <div className="field"><label>Phone</label><input value={form.phone} onChange={set('phone')} style={fieldStyle} /></div>
            </div>
          </div>
          <div className="modal-foot">
            <button type="button" className="btn ghost" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn primary" disabled={saving}>{saving ? 'Saving…' : '+ Add professional'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Financial entry modal ─────────────────────────────────────────────────────
function FinancialModal({ propertyId, entry, onClose, onSaved }) {
  const today = new Date().toISOString().split('T')[0];
  const [form, setForm] = useState({
    entry_date: entry?.entry_date || today,
    type: entry?.type || 'expense',
    category: entry?.category || '',
    description: entry?.description || '',
    amount: entry?.amount || '',
    gst_amount: entry?.gst_amount || '',
    invoice_number: entry?.invoice_number || '',
    notes: entry?.notes || '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  async function save(e) {
    e.preventDefault();
    if (!form.amount || parseFloat(form.amount) <= 0) { setError('Amount must be greater than 0'); return; }
    setSaving(true);
    setError('');
    try {
      const payload = {
        entry_date: form.entry_date,
        type: form.type,
        category: form.category || undefined,
        description: form.description || undefined,
        amount: parseFloat(form.amount),
        gst_amount: form.gst_amount ? parseFloat(form.gst_amount) : undefined,
        invoice_number: form.invoice_number || undefined,
        notes: form.notes || undefined,
      };
      if (entry) {
        await propertyAPI.updateFinancial(propertyId, entry.id, payload);
      } else {
        await propertyAPI.createFinancial(propertyId, payload);
      }
      onSaved();
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not save');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 580 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <h2 className="serif" style={{ fontSize: 20, margin: 0, color: 'var(--forest)', fontWeight: 600 }}>
            {entry ? 'Edit Entry' : 'Add Financial Entry'}
          </h2>
          <button onClick={onClose} className="btn ghost sm">✕</button>
        </div>
        <form onSubmit={save}>
          <div className="modal-body" style={{ display: 'grid', gap: 12 }}>
            {error && <div style={{ color: '#dc2626', fontSize: 13 }}>{error}</div>}

            <div style={{ display: 'flex', gap: 10 }}>
              {['income', 'expense'].map((t) => (
                <button
                  key={t} type="button"
                  onClick={() => setForm({ ...form, type: t })}
                  style={{
                    flex: 1, padding: '10px 0', border: '2px solid', borderRadius: 6, cursor: 'pointer', fontWeight: 600, fontSize: 13,
                    borderColor: form.type === t ? (t === 'income' ? '#15803d' : '#dc2626') : 'var(--hairline)',
                    background: form.type === t ? (t === 'income' ? '#f0fdf4' : '#fef2f2') : 'white',
                    color: form.type === t ? (t === 'income' ? '#15803d' : '#dc2626') : 'var(--ink-4)',
                  }}
                >
                  {t === 'income' ? '↑ Income' : '↓ Expense'}
                </button>
              ))}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div className="field"><label>Date *</label><input type="date" value={form.entry_date} onChange={set('entry_date')} required style={fieldStyle} /></div>
              <div className="field">
                <label>Category</label>
                <select value={form.category} onChange={set('category')} style={fieldStyle}>
                  <option value="">— select —</option>
                  {FIN_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>
            <div className="field">
              <label>Description</label>
              <input value={form.description} onChange={set('description')} placeholder="Brief description of this entry" style={fieldStyle} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
              <div className="field"><label>Amount ($) *</label><input type="number" value={form.amount} onChange={set('amount')} min={0.01} step={0.01} required style={fieldStyle} /></div>
              <div className="field"><label>GST ($)</label><input type="number" value={form.gst_amount} onChange={set('gst_amount')} min={0} step={0.01} style={fieldStyle} /></div>
              <div className="field"><label>Invoice #</label><input value={form.invoice_number} onChange={set('invoice_number')} placeholder="INV-0001" style={fieldStyle} /></div>
            </div>
            <div className="field">
              <label>Notes</label>
              <textarea value={form.notes} onChange={set('notes')} placeholder="Additional notes…" style={{ ...fieldStyle, height: 60, resize: 'vertical', paddingTop: 8 }} />
            </div>
          </div>
          <div className="modal-foot">
            <button type="button" className="btn ghost" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn primary" disabled={saving}>{saving ? 'Saving…' : entry ? '✓ Save changes' : '+ Add entry'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Doc type labels ───────────────────────────────────────────────────────────
const ENTRY_DOC_TYPES = [
  { value: 'other',            label: 'Document' },
  { value: 'invoice',          label: 'Invoice / Receipt' },
  { value: 'permit',           label: 'Permit' },
  { value: 'warranty',         label: 'Warranty' },
  { value: 'inspection_report',label: 'Inspection Report' },
  { value: 'photo',            label: 'Photo' },
  { value: 'certificate',      label: 'Certificate' },
];

function docTypeLabel(val) {
  return ENTRY_DOC_TYPES.find((d) => d.value === val)?.label || val;
}

// ── Entry card ────────────────────────────────────────────────────────────────
function EntryCard({ entry, propertyId, canWrite, onEdit, onDeleted }) {
  const [expanded, setExpanded] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [docs, setDocs] = useState(entry.documents || []);
  const meta = TYPE_META[entry.entry_type] || {};

  async function handleDelete() {
    if (!window.confirm(`Delete "${entry.title}"? This cannot be undone.`)) return;
    setDeleting(true);
    try {
      await timelineAPI.delete(propertyId, entry.id);
      onDeleted();
    } catch (e) {
      alert(e.response?.data?.detail || 'Delete failed');
      setDeleting(false);
    }
  }

  async function handleDeleteDoc(docId) {
    await timelineAPI.deleteDocument(propertyId, entry.id, docId);
    setDocs((prev) => prev.filter((d) => d.id !== docId));
  }

  return (
    <Panel className="lift" style={{ padding: 0, overflow: 'hidden' }}>
      {/* ── Collapsed row ── */}
      <div
        style={{ padding: 16, cursor: 'pointer', display: 'grid', gridTemplateColumns: '1fr auto', gap: 12 }}
        onClick={() => setExpanded((e) => !e)}
      >
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
            <Chip variant={meta.chip}>{meta.label}</Chip>
            {entry.verification_status === 'verified' && (
              <Chip variant="forest" style={{ fontSize: 10, height: 18, padding: '0 6px' }}>✓ Verified</Chip>
            )}
            {entry.is_featured && <Chip variant="amber">Major</Chip>}
            {docs.length > 0 && (
              <Chip style={{ fontSize: 10, height: 18, padding: '0 6px' }}>
                📎 {docs.length} doc{docs.length !== 1 ? 's' : ''}
              </Chip>
            )}
          </div>
          <div className="serif" style={{ fontSize: 17, fontWeight: 600, color: 'var(--ink)' }}>{entry.title}</div>
          {!expanded && entry.summary && (
            <div style={{ fontSize: 12, color: 'var(--ink-4)', marginTop: 3, maxWidth: 560 }}>
              {entry.summary.length > 100 ? entry.summary.slice(0, 100) + '…' : entry.summary}
            </div>
          )}
          {!expanded && entry.performed_by && (
            <div style={{ fontSize: 11, color: 'var(--ink-4)', marginTop: 4 }}>{entry.performed_by}</div>
          )}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8, flexShrink: 0 }}>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 11, color: 'var(--ink-4)', textTransform: 'uppercase' }}>
              {fmtDate(entry.entry_date, { short: true })}
            </div>
            <div className="serif tnum" style={{ fontSize: 20, fontWeight: 600, marginTop: 2 }}>
              {fmtCost(entry.cost)}
            </div>
          </div>
          {canWrite && (
            <div style={{ display: 'flex', gap: 4 }} onClick={(e) => e.stopPropagation()}>
              <button
                className="btn ghost sm"
                style={{ fontSize: 11, padding: '3px 8px' }}
                onClick={() => onEdit(entry)}
              >
                Edit
              </button>
              <button
                className="btn ghost sm"
                style={{ fontSize: 11, padding: '3px 8px', color: '#dc2626' }}
                onClick={handleDelete}
                disabled={deleting}
              >
                {deleting ? '…' : 'Delete'}
              </button>
            </div>
          )}
          <span style={{ fontSize: 12, color: 'var(--ink-4)' }}>{expanded ? '▲' : '▼'}</span>
        </div>
      </div>

      {/* ── Expanded detail ── */}
      {expanded && (
        <div style={{ borderTop: '1px solid var(--hairline)', padding: '16px 16px 18px', background: '#fafafa' }}>
          {entry.summary && (
            <p style={{ margin: '0 0 12px', fontSize: 13, color: 'var(--ink-2)', lineHeight: 1.6 }}>{entry.summary}</p>
          )}
          <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', marginBottom: 12 }}>
            {entry.performed_by && (
              <div>
                <div style={{ fontSize: 10, color: 'var(--ink-4)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 2 }}>Performed by</div>
                <div style={{ fontSize: 13, color: 'var(--ink)' }}>{entry.performed_by}</div>
              </div>
            )}
            {entry.tags?.filter(Boolean).length > 0 && (
              <div>
                <div style={{ fontSize: 10, color: 'var(--ink-4)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 4 }}>Tags</div>
                <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                  {entry.tags.filter(Boolean).map((t) => (
                    <Chip key={t} style={{ fontSize: 10, height: 18, padding: '0 6px' }}>{t}</Chip>
                  ))}
                </div>
              </div>
            )}
          </div>

          {entry.notes && (
            <div style={{ marginBottom: 14, padding: '10px 12px', background: '#fff', border: '1px solid var(--hairline)', borderRadius: 6 }}>
              <div style={{ fontSize: 10, color: 'var(--ink-4)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 6 }}>Notes</div>
              <pre style={{ margin: 0, fontSize: 12, color: 'var(--ink-3)', lineHeight: 1.6, whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}>
                {entry.notes}
              </pre>
            </div>
          )}

          {/* Documents */}
          {docs.length > 0 && (
            <div>
              <div style={{ fontSize: 10, color: 'var(--ink-4)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 8 }}>Attached Documents</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {docs.map((doc) => (
                  <div key={doc.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 10px', background: '#fff', border: '1px solid var(--hairline)', borderRadius: 6 }}>
                    <span style={{ fontSize: 16 }}>
                      {doc.mime_type?.startsWith('image/') ? '🖼️' : doc.mime_type === 'application/pdf' ? '📄' : '📎'}
                    </span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <a
                        href={`${API_BASE}${doc.file_url}`}
                        target="_blank"
                        rel="noreferrer"
                        style={{ fontSize: 13, color: 'var(--forest)', textDecoration: 'none', fontWeight: 500 }}
                      >
                        {doc.original_filename}
                      </a>
                      <div style={{ fontSize: 11, color: 'var(--ink-4)' }}>
                        {docTypeLabel(doc.document_type)}
                        {doc.file_size ? ` · ${(doc.file_size / 1024).toFixed(0)} KB` : ''}
                        {doc.is_before_photo && ' · Before photo'}
                        {doc.is_after_photo && ' · After photo'}
                      </div>
                    </div>
                    {canWrite && (
                      <button
                        onClick={() => handleDeleteDoc(doc.id)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626', fontSize: 12, flexShrink: 0 }}
                      >
                        Remove
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </Panel>
  );
}

// ── Combined Add / Edit entry modal ───────────────────────────────────────────
const ENTRY_SL = {
  fontSize: 10, letterSpacing: '0.16em', textTransform: 'uppercase',
  color: 'var(--ink-4)', fontWeight: 600, marginBottom: 10,
  paddingBottom: 6, borderBottom: '1px solid var(--hairline)',
};
const ENTRY_FS = {
  height: 38, padding: '0 10px', border: '1px solid var(--hairline)',
  borderRadius: 4, background: '#fff', fontSize: 13, outline: 'none', width: '100%',
};

function EntryModal({ propertyId, entry, onClose, onSaved }) {
  const isEdit = !!entry;
  const today = new Date().toISOString().split('T')[0];

  const [form, setForm] = useState({
    entry_type: entry?.entry_type || 'service',
    title: entry?.title || '',
    summary: entry?.summary || '',
    entry_date: entry?.entry_date || today,
    performed_by: entry?.performed_by || '',
    cost: entry?.cost != null ? String(entry.cost) : '',
    is_featured: entry?.is_featured || false,
    notes: isEdit ? (entry?.notes || '') : '',
  });

  // Add mode only — structured notes
  const [trade, setTrade] = useState(entry?.tags?.[0] || '');
  const [issues, setIssues] = useState(['']);
  const [invoice, setInvoice] = useState({ number: '', date: '', amount: '' });
  const [warranty, setWarranty] = useState({ provider: '', period: '', terms: '' });

  // Docs
  const [existingDocs, setExistingDocs] = useState(entry?.documents || []);
  const [queuedFiles, setQueuedFiles] = useState([]);  // [{file, type, isBefore, isAfter}]
  const fileRef = useRef();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [uploadStatus, setUploadStatus] = useState('');

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  function addFile(e) {
    const files = Array.from(e.target.files);
    const oversized = files.filter((f) => f.size > MAX_UPLOAD_BYTES);
    if (oversized.length > 0) {
      setUploadStatus(
        `Rejected (exceeds ${MAX_UPLOAD_LABEL}): ${oversized.map((f) => f.name).join(', ')}`
      );
      e.target.value = '';
      return;
    }
    setQueuedFiles((prev) => [...prev, ...files.map((f) => ({ file: f, type: 'other', isBefore: false, isAfter: false }))]);
    e.target.value = '';
  }

  function updateQueued(i, patch) {
    setQueuedFiles((prev) => prev.map((q, idx) => idx === i ? { ...q, ...patch } : q));
  }

  async function deleteExistingDoc(docId) {
    await timelineAPI.deleteDocument(propertyId, entry.id, docId);
    setExistingDocs((prev) => prev.filter((d) => d.id !== docId));
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { setError('Title is required'); return; }
    setLoading(true);
    setError('');

    let notes = form.notes;
    if (!isEdit) {
      const parts = [];
      const filledIssues = issues.filter((s) => s.trim());
      if (filledIssues.length) parts.push('Issues:\n' + filledIssues.map((s) => `• ${s}`).join('\n'));
      if (invoice.number || invoice.date || invoice.amount) {
        const p = ['Invoice:'];
        if (invoice.number) p.push(`Invoice #: ${invoice.number}`);
        if (invoice.date) p.push(`Date: ${invoice.date}`);
        if (invoice.amount) p.push(`Amount: $${parseFloat(invoice.amount).toLocaleString()}`);
        parts.push(p.join('\n'));
      }
      if (warranty.provider || warranty.period || warranty.terms) {
        const p = ['Warranty:'];
        if (warranty.provider) p.push(`Provider: ${warranty.provider}`);
        if (warranty.period) p.push(`Period: ${warranty.period}`);
        if (warranty.terms) p.push(`Terms: ${warranty.terms}`);
        parts.push(p.join('\n'));
      }
      notes = parts.length ? parts.join('\n\n') : null;
    }

    try {
      let savedId;
      const cost = form.cost ? parseFloat(form.cost) : null;

      if (isEdit) {
        const payload = {
          title: form.title, summary: form.summary || null,
          entry_date: form.entry_date, performed_by: form.performed_by || null,
          cost, is_featured: form.is_featured, notes: notes || null,
          tags: trade ? [trade] : (entry?.tags || []),
        };
        const { data } = await timelineAPI.update(propertyId, entry.id, payload);
        savedId = data.id;
      } else {
        const { data } = await timelineAPI.create(propertyId, {
          entry_type: form.entry_type, title: form.title,
          summary: form.summary || null, entry_date: form.entry_date,
          performed_by: form.performed_by || null, cost,
          is_featured: form.is_featured, notes,
          tags: trade ? [trade] : [],
        });
        savedId = data.id;
      }

      // Sync financial entry if cost is provided
      if (cost && cost > 0) {
        try {
          // Map entry type to financial category
          const categoryMap = {
            'renovation': 'Renovation',
            'service': 'Maintenance',
            'inspection': 'Maintenance',
            'compliance': 'Other Expense',
          };

          const category = categoryMap[form.entry_type] || 'Other Expense';
          const financialPayload = {
            entry_date: form.entry_date,
            type: 'expense',
            category,
            description: form.title,
            amount: cost,
            notes: form.summary ? `${form.summary}\n[Synced from Redbook]` : '[Synced from Redbook]',
          };

          // Check if financial entry already exists for this redbook entry
          if (entry?.financial_entry_id) {
            // Update existing financial entry
            await propertyAPI.updateFinancial(propertyId, entry.financial_entry_id, financialPayload);
          } else if (isEdit) {
            // For edits without existing financial entry, don't auto-create
            // (user may have intentionally not created one)
          } else {
            // For new entries, create financial entry
            // Note: The backend should return the financial_entry_id in the response
            // For now, we'll create it but can't link it back without API enhancement
            await propertyAPI.createFinancial(propertyId, financialPayload);
          }
        } catch (finErr) {
          // Don't fail the redbook entry if financial sync fails
          console.warn('Financial entry sync failed:', finErr);
        }
      }

      // Upload queued documents
      if (queuedFiles.length > 0) {
        for (let i = 0; i < queuedFiles.length; i++) {
          const q = queuedFiles[i];
          setUploadStatus(`Uploading ${i + 1}/${queuedFiles.length}: ${q.file.name}`);
          const fd = new FormData();
          fd.append('file', q.file);
          fd.append('document_type', q.type);
          fd.append('is_before_photo', String(q.isBefore));
          fd.append('is_after_photo', String(q.isAfter));
          await timelineAPI.uploadDocument(propertyId, savedId, fd);
        }
      }
      onSaved();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save');
    } finally {
      setLoading(false);
      setUploadStatus('');
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 700, maxHeight: '90vh', display: 'flex', flexDirection: 'column' }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <h2 className="serif" style={{ fontSize: 22, margin: 0, color: 'var(--forest)', fontWeight: 600 }}>
            {isEdit ? 'Edit Entry' : 'Add to Redbook'}
          </h2>
          <button onClick={onClose} className="btn ghost sm">✕</button>
        </div>

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
          <div className="modal-body" style={{ display: 'grid', gap: 18, overflowY: 'auto', flex: 1, paddingRight: 4 }}>
            {error && (
              <div style={{ color: '#dc2626', fontSize: 13, background: '#fef2f2', padding: '8px 12px', borderRadius: 4 }}>{error}</div>
            )}

            {/* Entry type (add only) */}
            {!isEdit && (
              <div>
                <div style={ENTRY_SL}>Entry Type</div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {['service', 'renovation', 'inspection', 'compliance'].map((t) => (
                    <Chip key={t} active={form.entry_type === t}
                      onClick={() => setForm({ ...form, entry_type: t })}
                      style={{ cursor: 'pointer', height: 30, padding: '0 12px', fontSize: 12 }}>
                      {TYPE_META[t]?.label || t}
                    </Chip>
                  ))}
                </div>
              </div>
            )}

            {/* Work details */}
            <div>
              <div style={ENTRY_SL}>Work Details</div>
              <div style={{ display: 'grid', gap: 12 }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
                  <div className="field">
                    <label>Trade</label>
                    <select value={trade} onChange={(e) => setTrade(e.target.value)} style={ENTRY_FS}>
                      {TRADES.map((t) => <option key={t} value={t}>{t || 'Select trade…'}</option>)}
                    </select>
                  </div>
                  <div className="field">
                    <label>Performed By</label>
                    <input value={form.performed_by} onChange={set('performed_by')} placeholder="Vendor or company name" style={ENTRY_FS} />
                  </div>
                </div>
                <div className="field">
                  <label>Title *</label>
                  <input value={form.title} onChange={set('title')} required placeholder="e.g. Replaced hot water heater" style={ENTRY_FS} />
                </div>
                <div className="field">
                  <label>Summary</label>
                  <textarea value={form.summary} onChange={set('summary')} placeholder="Brief description of what was done…"
                    style={{ ...ENTRY_FS, height: 'auto', minHeight: 64, paddingTop: 8, resize: 'vertical' }} />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12, alignItems: 'flex-end' }}>
                  <div className="field"><label>Date</label><input type="date" value={form.entry_date} onChange={set('entry_date')} style={ENTRY_FS} /></div>
                  <div className="field"><label>Cost ($)</label><input type="number" value={form.cost} onChange={set('cost')} placeholder="0.00" min={0} step={0.01} style={ENTRY_FS} /></div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, paddingBottom: 2 }}>
                    <input type="checkbox" checked={form.is_featured} onChange={(e) => setForm({ ...form, is_featured: e.target.checked })}
                      style={{ accentColor: 'var(--forest)' }} />
                    Major work
                  </label>
                </div>
              </div>
            </div>

            {/* Notes — structured for add, free-text for edit */}
            {!isEdit ? (
              <>
                <div>
                  <div style={{ ...ENTRY_SL, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>Issues / Line Items</span>
                    <button type="button" className="btn ghost sm" onClick={() => setIssues([...issues, ''])}
                      style={{ fontSize: 11, textTransform: 'none', letterSpacing: 0 }}>+ Add issue</button>
                  </div>
                  {issues.map((issue, i) => (
                    <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                      <input value={issue} onChange={(e) => setIssues(issues.map((v, j) => j === i ? e.target.value : v))}
                        placeholder={`Issue or work item ${i + 1}`}
                        style={{ flex: 1, height: 36, padding: '0 10px', border: '1px solid var(--hairline)', borderRadius: 4, fontSize: 13, outline: 'none' }} />
                      {issues.length > 1 && (
                        <button type="button" onClick={() => setIssues(issues.filter((_, j) => j !== i))}
                          style={{ width: 28, height: 28, border: '1px solid var(--hairline)', borderRadius: 4, background: 'transparent', cursor: 'pointer', fontSize: 14, color: 'var(--ink-4)' }}>×</button>
                      )}
                    </div>
                  ))}
                </div>
                <div>
                  <div style={ENTRY_SL}>Invoice Details</div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12 }}>
                    <div className="field"><label>Invoice #</label><input value={invoice.number} onChange={(e) => setInvoice({ ...invoice, number: e.target.value })} placeholder="INV-12345" style={ENTRY_FS} /></div>
                    <div className="field"><label>Invoice Date</label><input type="date" value={invoice.date} onChange={(e) => setInvoice({ ...invoice, date: e.target.value })} style={ENTRY_FS} /></div>
                    <div className="field"><label>Invoice Amount ($)</label><input type="number" value={invoice.amount} onChange={(e) => setInvoice({ ...invoice, amount: e.target.value })} placeholder="0.00" min={0} step={0.01} style={ENTRY_FS} /></div>
                  </div>
                </div>
                <div>
                  <div style={ENTRY_SL}>Warranty Details</div>
                  <div style={{ display: 'grid', gap: 10 }}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
                      <div className="field"><label>Provider</label><input value={warranty.provider} onChange={(e) => setWarranty({ ...warranty, provider: e.target.value })} placeholder="Company or manufacturer" style={ENTRY_FS} /></div>
                      <div className="field"><label>Period</label><input value={warranty.period} onChange={(e) => setWarranty({ ...warranty, period: e.target.value })} placeholder="e.g. 1 year parts & labor" style={ENTRY_FS} /></div>
                    </div>
                    <div className="field"><label>Warranty Terms / Notes</label><textarea value={warranty.terms} onChange={(e) => setWarranty({ ...warranty, terms: e.target.value })} placeholder="Coverage details, exclusions…" style={{ ...ENTRY_FS, height: 'auto', minHeight: 56, paddingTop: 8, resize: 'vertical' }} /></div>
                  </div>
                </div>
              </>
            ) : (
              <div>
                <div style={ENTRY_SL}>Notes</div>
                <textarea value={form.notes} onChange={set('notes')} placeholder="Notes, issues, invoice details, warranty info…"
                  style={{ ...ENTRY_FS, height: 'auto', minHeight: 100, paddingTop: 8, resize: 'vertical' }} />
              </div>
            )}

            {/* Documents section */}
            <div>
              <div style={{ ...ENTRY_SL, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>Documents</span>
                <button type="button" className="btn ghost sm" onClick={() => fileRef.current?.click()}
                  style={{ fontSize: 11, textTransform: 'none', letterSpacing: 0 }}>
                  📎 Attach file
                </button>
                <input ref={fileRef} type="file" multiple accept={ACCEPT_ALL} style={{ display: 'none' }} onChange={addFile} />
              </div>

              {/* Existing docs (edit mode) */}
              {isEdit && existingDocs.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 10 }}>
                  {existingDocs.map((doc) => (
                    <div key={doc.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 10px', background: '#f9fafb', border: '1px solid var(--hairline)', borderRadius: 6 }}>
                      <span style={{ fontSize: 15 }}>{doc.mime_type?.startsWith('image/') ? '🖼️' : '📄'}</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <a href={`${API_BASE}${doc.file_url}`} target="_blank" rel="noreferrer"
                          style={{ fontSize: 13, color: 'var(--forest)', textDecoration: 'none' }}>
                          {doc.original_filename}
                        </a>
                        <div style={{ fontSize: 11, color: 'var(--ink-4)' }}>{docTypeLabel(doc.document_type)}</div>
                      </div>
                      <button type="button" onClick={() => deleteExistingDoc(doc.id)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626', fontSize: 12 }}>Remove</button>
                    </div>
                  ))}
                </div>
              )}

              {/* Queued new files */}
              {queuedFiles.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {queuedFiles.map((q, i) => (
                    <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '10px', background: '#fff', border: '1px dashed #d1d5db', borderRadius: 6 }}>
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                        <span style={{ fontSize: 15, flexShrink: 0 }}>📎</span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--ink)', overflow: 'hidden', textOverflow: 'ellipsis', wordBreak: 'break-word' }}>{q.file.name}</div>
                          <div style={{ fontSize: 11, color: 'var(--ink-4)' }}>{(q.file.size / 1024).toFixed(0)} KB</div>
                        </div>
                        <button type="button" onClick={() => setQueuedFiles((prev) => prev.filter((_, j) => j !== i))}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626', fontSize: 16, flexShrink: 0 }}>×</button>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 8, alignItems: 'center' }}>
                        <select value={q.type} onChange={(e) => updateQueued(i, { type: e.target.value })}
                          style={{ ...ENTRY_FS, height: 32, fontSize: 12 }}>
                          {ENTRY_DOC_TYPES.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
                        </select>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: 'var(--ink-4)', cursor: 'pointer' }}>
                          <input type="checkbox" checked={q.isBefore} onChange={(e) => updateQueued(i, { isBefore: e.target.checked })} style={{ accentColor: 'var(--forest)' }} />
                          Before
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: 'var(--ink-4)', cursor: 'pointer' }}>
                          <input type="checkbox" checked={q.isAfter} onChange={(e) => updateQueued(i, { isAfter: e.target.checked })} style={{ accentColor: 'var(--forest)' }} />
                          After
                        </label>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {queuedFiles.length === 0 && (!isEdit || existingDocs.length === 0) && (
                <div style={{ fontSize: 12, color: 'var(--ink-4)', textAlign: 'center', padding: '12px 0' }}>
                  No files attached. Click "Attach file" to add invoices, photos, permits, and more.
                </div>
              )}
            </div>
          </div>

          <div className="modal-foot">
            {uploadStatus && <span style={{ fontSize: 12, color: 'var(--ink-4)', flex: 1 }}>{uploadStatus}</span>}
            <button type="button" className="btn ghost" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn primary" disabled={loading}>
              {loading ? (uploadStatus || 'Saving…') : isEdit ? '✓ Save changes' : '✓ Save to Redbook'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
